import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Td,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { BsThreeDotsVertical } from "react-icons/bs";
import DateRangePicker from "../../../../components/DateRangePicker/DateRangePicker";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { StyledTable } from "../../../../components/StyledTable/StyledTable";
import dayjs from "dayjs";
import { useDeactivateCodeYellow, useGetAdminGroupCodeYellowRecords } from "../../../../api/CodeYellowApi";
import { CYAlertRecord } from "../../../../types/CYAlertRecord";
import { CodeYellow, DistributionType } from "../../../../types/CodeYellow";
import { getTimeZoneAbbreviation, getUserTimeZone } from "../../../../utils/timeZones";
import ModalContainer from "../../../../components/ModalContainer/ModalContainer";
import EditCodeYellowModal from "../../../../components/CYModal/EditCodeYellowModal";
import { Worklist, WorklistType } from "../../../../types/Worklist";
import TargetedProvidersModal from "../../../../components/TargetedProvidersModal/TargetedProvidersModal";

interface Props {
  groupId: string;
}

interface TableColumn {
  key: keyof CYAlertRecord | "action";
  label: string;
}

const CYAlertsTable: React.FC<Props> = ({ groupId }) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [datesFilter, setDatesFilter] = useState<Date[]>([
    dayjs().startOf("month").toDate(),
    dayjs().endOf("day").toDate()
  ]);

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose
  } = useDisclosure();
  const [actionType, setActionType] = useState<"cancel" | "deactivate">();
  const [currentCodeYellowId, setCurrentCodeYellowId] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCodeYellow, setSelectedCodeYellow] = useState<CodeYellow | null>(null);

  const [targetedProvidersModalOpen, setTargetedProvidersModalOpen] = useState(false);
  const [selectedCodeYellowId, setSelectedCodeYellowId] = useState<string | null>(null);
  const [showSwitch, setShowSwitch] = useState(false);

  const { data, isLoading } = useGetAdminGroupCodeYellowRecords(
    page,
    perPage,
    groupId,
    datesFilter
  );

  const { mutate: deactivateCodeYellow } = useDeactivateCodeYellow();

  const tableColumns: TableColumn[] = [
    { key: "startTime", label: "Start Time" },
    { key: "endTime", label: "End Time" },
    { key: "activatedBy", label: "Activated By" },
    { key: "targetedProvidersCount", label: "Targeted" },
    { key: "usdPerRvu", label: "$/RVU" },
    { key: "totalCYRVUs", label: "RVUs" },
    { key: "totalAmountPayable", label: "Amount Payable" },
    { key: "totalAmountPaid", label: "Amount Paid" },
    { key: "status", label: "Status" },
    { key: "action", label: "" }
  ];

  const userTimeZone = getUserTimeZone();
  const userTimeZoneAbbreviation = getTimeZoneAbbreviation(userTimeZone);

  const handleOpenModal = useCallback(
    (type: "cancel" | "deactivate", codeYellowId: string) => {
      setActionType(type);
      setCurrentCodeYellowId(codeYellowId);
      onModalOpen();
    },
    [onModalOpen]
  );

  const handleConfirmAction = useCallback(() => {
    if (currentCodeYellowId) {
      deactivateCodeYellow({ codeYellowId: currentCodeYellowId });
    }
    onModalClose();
  }, [currentCodeYellowId, deactivateCodeYellow, onModalClose]);

  const getStatus = useCallback((cyAlertRecord: CYAlertRecord) => {
    const now = dayjs();
    const startTime = dayjs(cyAlertRecord.startTime);
    const endTime = cyAlertRecord.endTime ? dayjs(cyAlertRecord.endTime) : null;

    if (endTime && endTime.isBefore(now)) {
      return "Passed";
    } else if (startTime.isAfter(now)) {
      return "Scheduled";
    } else {
      return "Active";
    }
  }, []);

  const handleEdit = useCallback(
    (cyAlertRecord: CYAlertRecord) => {
      const worklist: Worklist = {
        id: cyAlertRecord.worklistId,
        type: WorklistType.GROUP,
        groupId: groupId,
        usdPerRvu: cyAlertRecord.usdPerRvu,
        codeYellows: []
      };

      const codeYellowData: CodeYellow = {
        id: cyAlertRecord.id,
        worklistId: cyAlertRecord.worklistId,
        worklist: worklist,
        usdPerRvu: cyAlertRecord.usdPerRvu,
        isActive: getStatus(cyAlertRecord) === "Active",
        startTime: new Date(cyAlertRecord.startTime),
        endTime: cyAlertRecord.endTime ? new Date(cyAlertRecord.endTime) : null,
        distributionType: cyAlertRecord.distributionType as DistributionType,
        respondingProviders: [],
        group: undefined,
        activatingProvider: undefined,
        limits: {
          amountLimit: cyAlertRecord.totalAmountPayable || undefined,
          RVUsLimit: cyAlertRecord.totalCYRVUs || undefined
        },
        targetedProvidersCount: cyAlertRecord.targetedProvidersCount,
        targetedProviders: [],
        rvusTotal: cyAlertRecord.totalCYRVUs || 0,
        amountTotal: cyAlertRecord.totalAmountPayable || 0,
        totalAmountPaid: cyAlertRecord.totalAmountPaid || 0,
        canManage: true
      };

      setSelectedCodeYellow(codeYellowData);
      setIsEditModalOpen(true);
    },
    [groupId, getStatus]
  );

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedCodeYellow(null);
  }, []);

  const handleOpenTargetedProvidersModal = useCallback((codeYellowId: string, status: string) => {
    setSelectedCodeYellowId(codeYellowId);
    setShowSwitch(status === "Active" || status === "Scheduled");
    setTargetedProvidersModalOpen(true);
  }, []);

  const customRowRenderers = useCallback(
    (cyAlertRecord: CYAlertRecord) => {
      const status = getStatus(cyAlertRecord);

      const color =
        status === "Active"
          ? "yellow.500"
          : status === "Scheduled"
            ? "orange.500"
            : "blue.500";

      return (
        <>
          <Td textStyle="smMdSemi">
            {`${dayjs(cyAlertRecord.startTime).format(
              "MM/DD/YYYY HH:mm"
            )} (${userTimeZoneAbbreviation})`}
          </Td>
          <Td textStyle="smMdSemi">
            {cyAlertRecord.endTime
              ? `${dayjs(cyAlertRecord.endTime).format(
                "MM/DD/YYYY HH:mm"
              )} (${userTimeZoneAbbreviation})`
              : "N/A"}
          </Td>
          <Td textStyle="smMdSemi">{cyAlertRecord.activatedBy}</Td>
          <Td textStyle="smMdSemi">
            <Text
              cursor="pointer"
              color="yellow.500"
              fontWeight="bold"
              onClick={() => handleOpenTargetedProvidersModal(cyAlertRecord.id, status)}
            >
              {cyAlertRecord.targetedProvidersCount || cyAlertRecord.groupProvidersCount}
            </Text>
          </Td>
          <Td textStyle="smMdSemi">{cyAlertRecord.usdPerRvu?.toFixed(2) ?? "N/A"}</Td>
          <Td textStyle="smMdSemi">{cyAlertRecord.totalCYRVUs?.toFixed(2) ?? "0"}</Td>
          <Td textStyle="smMdSemi">
            ${cyAlertRecord.totalAmountPayable?.toFixed(2) ?? "0.00"}
          </Td>
          <Td textStyle="smMdSemi">
            ${cyAlertRecord.totalAmountPaid?.toFixed(2) ?? "0.00"}
          </Td>
          <Td>
            <Text textStyle="smMdSemi" color={color}>
              {status}
            </Text>
          </Td>
          <Td>
            {status !== "Passed" && cyAlertRecord.canManage && (
              <Popover placement="bottom" closeOnBlur={true}>
                <PopoverTrigger>
                  <Button variant="ghost" size="sm">
                    <BsThreeDotsVertical />
                  </Button>
                </PopoverTrigger>
                <PopoverContent width="fit-content" bg="#373D59" border={"none"}>
                  {status === "Scheduled" && (
                    <Button
                      bg="#373D59"
                      color="white"
                      fontSize="md"
                      fontWeight="bold"
                      py={4}
                      px={6}
                      _hover={{
                        bg: "#2A2E45"
                      }}
                      onClick={() => handleEdit(cyAlertRecord)}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    bg="#373D59"
                    color="white"
                    fontSize="md"
                    fontWeight="bold"
                    py={4}
                    px={6}
                    _hover={{
                      bg: "#2A2E45"
                    }}
                    onClick={() =>
                      handleOpenModal(
                        status === "Scheduled" ? "cancel" : "deactivate",
                        cyAlertRecord.id
                      )
                    }
                  >
                    {status === "Scheduled" ? "Cancel" : "Deactivate"}
                  </Button>
                </PopoverContent>
              </Popover>
            )}
          </Td>
        </>
      );
    },
    [getStatus, handleOpenModal, handleEdit, userTimeZoneAbbreviation, handleOpenTargetedProvidersModal]
  );

  return (
    <Box>
      <HStack my="4" spacing={4}>
        <DateRangePicker initialDates={datesFilter} onDatesChange={setDatesFilter} />
      </HStack>
      <Box overflowX="scroll" minW="full">
        <StyledTable
          columns={tableColumns}
          data={data?.docs ?? []}
          customRowRenderers={customRowRenderers}
          loading={isLoading}
        />
      </Box>
      <Pagination pages={data?.totalPages ?? 0} currentPage={page} setPage={setPage} />

      <ModalContainer
        isOpen={isModalOpen}
        onClose={onModalClose}
        onClickLeftButton={onModalClose}
        onClickRightButton={handleConfirmAction}
        title={
          actionType === "cancel"
            ? "Are you sure you want to cancel this LnQ alert?"
            : "Are you sure you want to deactivate this LnQ alert?"
        }
        leftButtonTitle="Cancel"
        rightButtonTitle="Confirm"
      />

      {isEditModalOpen && selectedCodeYellow && (
        <EditCodeYellowModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          codeYellow={selectedCodeYellow}
        />
      )}

      <TargetedProvidersModal
        codeYellowId={selectedCodeYellowId}
        isOpen={targetedProvidersModalOpen}
        onClose={() => {
          setTargetedProvidersModalOpen(false);
          setSelectedCodeYellowId(null);
        }}
        showSwitch={showSwitch}
      />
    </Box>
  );
};

export default CYAlertsTable;